import { ContentLayout } from '../../layouts/ContentLayout/ContentLayout';
import { ColumnDef, Row } from '@tanstack/react-table';
import { ServerPaginatedTable } from '../../components/Table/ServerPaginatedTable';
import { useSortedTable } from '../../hooks/useSortedTable';
import { usePaginatedTable } from '../../hooks/usePaginatedTable';
import { Link } from 'react-router-dom';
import { FileTray } from 'react-ionicons';
import { useCallback, useEffect, useState } from 'react';
import format from 'date-fns/format';
import SearchBar from '../../components/Table/SearchBar';
import { GraniteSelect, OptionType } from '../../components/Select/Select';
import { useFilterForTable } from '../../hooks/useFilterForTable';
import { Chip } from '../../components/Chip/Chip';
import { useNavigate } from 'react-router-dom';
import PageTitle from 'components/PageTitle';
import { TokenUserPermissions } from 'api/users/schemas/Users';
import Filters from 'components/Filters';
import { MultiValue, SingleValue } from 'react-select';
import { filterByDate } from 'screens/Resources';
import { dateToUTCWithoutMS } from 'shared/util/dateToUtcStrWithoutMS';
import { endOfDay } from 'date-fns';
import { getCookie, setCookie } from 'shared/util/util';
import { DisplayForPermission } from 'components/Permission/DisplayForPermission';
import { useQueryParams } from 'hooks/useQueryParams';
import { sortOptionsAlphabetically } from 'shared/util/sortOptionsAlphabetically';
import {
  OrderResponse,
  OrdersFilterSearchParams,
  OrdersMetadata,
} from 'api/accessexpressv3/schemas/OrdersSchemas';
import { getOrdersIndex } from 'api/accessexpressv3/api';

const EmptyOrdersIndex = () => {
  return (
    <div className="col-span-full flex flex-col items-center gap-6 bg-background-base-surface-2 p-8">
      <FileTray
        color="rgb(var(--content-base-subdued))"
        width="48px"
        height="48px"
      />
      <p className="rounded font-bold leading-[22px] text-content-base-default">
        You haven&apos;t ordered any services yet.
      </p>
    </div>
  );
};

export const EmptySearchResults = () => {
  return (
    <div className="col-span-full flex flex-col items-center gap-6 bg-background-base-surface-2 p-8">
      <svg
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M35.6785 8.46867C36.5629 7.58427 36.5629 6.15058 35.6785 5.26663C34.7941 4.38269 33.3609 4.38269 32.4765 5.26663L5.26618 32.4765C4.38223 33.3609 4.38223 34.7946 5.26618 35.6785C5.7086 36.1205 6.28779 36.3419 6.86742 36.3419C7.44706 36.3419 8.02624 36.1205 8.46867 35.6785L35.6785 8.46867ZM33.3278 30.7661C35.5889 27.9458 36.9442 24.3684 36.9442 20.4726C36.9442 17.5699 36.1911 14.8447 34.8729 12.4763L32.174 15.1752C32.9089 16.7919 33.3215 18.5842 33.3215 20.4726C33.3215 27.5577 27.5577 33.3219 20.4726 33.3219C18.5842 33.3219 16.7914 32.9089 15.1752 32.1744L12.4763 34.8729C14.8447 36.1911 17.5699 36.9447 20.4726 36.9447C24.3684 36.9447 27.9458 35.5889 30.7657 33.3278L41.6624 44.2245C42.0165 44.5782 42.4797 44.7548 42.9434 44.7548C43.4072 44.7548 43.8704 44.5782 44.2245 44.2245C44.9319 43.5167 44.9319 42.3702 44.2245 41.6628L33.3278 30.7661ZM6.07178 28.4688C4.75402 26.1005 4.00049 23.3753 4.00049 20.4726C4.00049 11.3755 11.3755 4.00049 20.4726 4.00049C23.3748 4.00049 26.1005 4.75402 28.4688 6.07178L25.7699 8.77071C24.1533 8.03621 22.3609 7.62322 20.4726 7.62322C13.3874 7.62322 7.62322 13.3874 7.62322 20.4726C7.62322 22.3609 8.03621 24.1533 8.77071 25.7699L6.07178 28.4688Z"
          fill="#94A3B8"
        />
      </svg>
      <p className="rounded font-bold leading-[22px] text-content-base-default">
        No results
      </p>
    </div>
  );
};

const getOrdersIndexTableDef = (): ColumnDef<OrderResponse>[] => {
  const columns: ColumnDef<OrderResponse>[] = [
    {
      header: 'Name',
      accessorKey: 'order_name',
    },
    {
      header: 'Submitted by',
      accessorKey: 'requester',
    },
    {
      header: 'Order #',
      accessorKey: 'order_number',
      cell: (props) =>
        props.getValue() ? (
          <span className="h-6 rounded-lg bg-background-base-surface-3 px-2 text-xs font-bold leading-6">
            #{props.getValue() as string}
          </span>
        ) : (
          ''
        ),
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: (props) => {
        const status = props.row.original.status;
        return (
          <div className="flex items-center justify-center gap-2 text-sm">
            <span className="min-h-[10px] min-w-[10px] rounded-full bg-green-400" />
            <div>{status === 'submitted' ? 'Order submitted' : status}</div>
          </div>
        );
      },
      enableSorting: false,
    },
    {
      header: 'Last updated',
      accessorKey: 'updated_at',
      cell: ({ getValue }) => {
        const date = getValue() as string | undefined;

        if (!date) {
          return <span className="text-sm text-content-base-subdued">N/A</span>;
        }

        const formattedDate = format(
          typeof date === 'string' ? new Date(date) : date,
          'MM/dd/yyyy',
        );
        return formattedDate;
      },
    },
    {
      header: 'Date submitted',
      accessorKey: 'created_at',
      cell: ({ getValue }) => {
        const date = getValue() as string | undefined;

        if (!date) {
          return <span className="text-sm text-content-base-subdued">N/A</span>;
        }

        const formattedDate = format(
          typeof date === 'string' ? new Date(date) : date,
          'MM/dd/yyyy',
        );
        return formattedDate;
      },
    },
  ];

  return columns;
};

export const OrdersIndex = () => {
  const { getQueryParam, setQueryParam, resetQueryParams } = useQueryParams({
    search: {
      defaultValue: '',
      getParam: (value) => value,
      setParam: (value) => value,
    },
    requester: {
      defaultValue: [] as MultiValue<OptionType>,
      getParam: (value) =>
        value ? value.split(',').map((v) => ({ label: v, value: v })) : [],
      setParam: (value) => value.map(({ value }) => value).join(','),
    },
    date: {
      defaultValue: null as SingleValue<OptionType>,
      getParam: (value) => ({
        label: value,
        value: filterByDate.find((f) => f.label === value)?.value || '',
      }),
      setParam: (value) => (value ? value.label : ''),
    },
  });

  const [search, setSearch] = useState(getQueryParam('search'));
  const [pageSize, setPageSize] = useState<number>();
  const navigate = useNavigate();
  const [dateFilter, setDateFilter] = useState<SingleValue<OptionType>>(
    getQueryParam('date'),
  );

  const { sortingQueryParams, sortingState, onSortingChange } = useSortedTable({
    initialSorting: [{ id: 'created_at', desc: true }],
  });

  const {
    queryParamFilter: requesterFilter,
    clearFilter: clearRequesterFilter,
    ...requesterFilterProps
  } = useFilterForTable({
    queryParamKey: 'requester',
    defaultValue: getQueryParam('requester'),
    onAfterChange: (values) => setQueryParam('requester', values),
  });

  const handleDateFilterChange = useCallback(
    (value: SingleValue<OptionType>) => {
      setDateFilter(value);
      setQueryParam('date', value);
    },
    [setQueryParam],
  );

  const getOrdersIndexFn = useCallback(
    (args?: OrdersFilterSearchParams) => {
      return getOrdersIndex({
        ...(search ? { search } : {}),
        ...(dateFilter
          ? {
              start_date: dateFilter.value,
              end_date: dateToUTCWithoutMS(endOfDay(new Date())),
            }
          : {}),
        ...sortingQueryParams,
        ...args,
        ...requesterFilter,
      });
    },
    [dateFilter, search, sortingQueryParams, requesterFilter],
  );

  const {
    data: tablePageData,
    metadata,
    ...paginatedTableProps
  } = usePaginatedTable<OrderResponse, OrdersMetadata>(
    getOrdersIndexFn,
    {
      search,
      sortingQueryParams,
    },
    [
      'orders-index-table',
      sortingQueryParams,
      search,
      dateFilter,
      requesterFilter,
    ],
    {
      refetchOnMount: true,
    },
  );

  const breadcrumbs = [
    {
      icon: 'home',
      label: 'Order services',
      onClick: () => navigate('/access-express/v3/order-services/access'),
    },
    {
      label: 'My orders',
    },
  ];

  const clearAllFilters = useCallback(() => {
    handleDateFilterChange(null);
    clearRequesterFilter();
    resetQueryParams();
  }, [clearRequesterFilter, handleDateFilterChange, resetQueryParams]);

  const handleRowClick = (row: Row<OrderResponse>) => {
    navigate(`/access-express/v3/${row?.original?.id}`);
  };
  useEffect(() => {
    const savedSize = getCookie('paginationSizeAccessExpressV3');
    if (savedSize) {
      setPageSize(parseInt(savedSize, 10));
    }
  }, []);

  const paginationChanged = (page: number) => {
    setPageSize(page);
    setCookie('paginationSizeAccessExpress', page.toString(), 365);
  };

  return (
    <ContentLayout>
      <div className="mb-11 flex justify-between">
        <PageTitle
          className="w-full"
          title="My orders"
          breadcrumbs={breadcrumbs}
        >
          <div className="my-auto flex gap-4">
            <DisplayForPermission
              permission={TokenUserPermissions.ACCESS_EXPRESS_WRITE}
              upgradeTooltip
            >
              <Link
                className="button primary large"
                to="/access-express/v3/order-services/access"
              >
                Order services
              </Link>
            </DisplayForPermission>
          </div>
        </PageTitle>
      </div>
      <h2 className="mb-8 text-[28px] font-bold leading-9 text-content-base-default">
        Order history
      </h2>
      <div className="mb-4 flex w-full flex-wrap items-start justify-between gap-4">
        <div className="w-full sm:flex-1">
          <SearchBar
            placeholder="Search by name or order #"
            onSearch={(query) => {
              setSearch(query);
              setQueryParam('search', query);
            }}
            onQueryClear={() => {
              setSearch('');
              setQueryParam('search', '');
            }}
            clearAllValues={search === ''}
            defaultQuery={search}
          />
        </div>

        <Filters
          clearFilters={clearAllFilters}
          clearFilterClassName="col-span-1"
        >
          <GraniteSelect
            isMulti
            className="col-span-1"
            placeholder="Filter by requester"
            options={
              metadata && metadata.requesters
                ? sortOptionsAlphabetically(
                    metadata.requesters.map((option) => ({
                      label: option,
                      value: option,
                    })),
                  )
                : []
            }
            controlShouldRenderValue={false}
            isSearchable={false}
            {...requesterFilterProps}
          />
          <GraniteSelect
            options={filterByDate}
            isClearable={false}
            isSearchable={false}
            className="col-span-1"
            placeholder="Filter by date submitted"
            controlShouldRenderValue={false}
            onChange={handleDateFilterChange}
            value={dateFilter}
          />
        </Filters>
      </div>
      <div className="gap mb-12 flex gap-4">
        {requesterFilterProps.value.map((filter) => (
          <Chip
            key={filter.value}
            label={filter.label}
            className="mt-4"
            onDelete={() => {
              requesterFilterProps.onChange(
                requesterFilterProps.value.filter(
                  (v) => v.value !== filter.value,
                ),
              );
            }}
          />
        ))}
        {dateFilter?.value && (
          <Chip
            label={dateFilter.label}
            onDelete={() => handleDateFilterChange(null)}
            className="mt-4"
          />
        )}
      </div>
      <ServerPaginatedTable
        data={tablePageData as OrderResponse[]}
        columns={getOrdersIndexTableDef()}
        title="orders index table"
        sortingState={sortingState}
        onSortingChange={onSortingChange}
        showHeadersWhenEmpty={false}
        emptyDataElement={
          search || requesterFilterProps.value.length > 0 || dateFilter ? (
            <EmptySearchResults />
          ) : (
            <EmptyOrdersIndex />
          )
        }
        handleRowClick={handleRowClick}
        paginationChanged={paginationChanged}
        paginationSizeStored={pageSize}
        {...paginatedTableProps}
      />
    </ContentLayout>
  );
};
